/** SUNTRONIX ONE — Warranty Nudges.
 *
 *  Lists Concierge-style warranty renewal nudges created by the
 *  nightly `warranty-renewal-nudge` cron (04:30 UTC).  Each row is
 *  one critical device whose warranty ends inside the 25-35 day
 *  window — the owner picks an outcome (renewed · extended · replaced
 *  · let_lapse · dismissed) which resolves the nudge and closes the
 *  audit loop.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ShieldAlert, ExternalLink, CheckCircle2, RefreshCcw } from "lucide-react";

const OUTCOMES = [
  ["renewed",   "Renewed",         "Warranty renewed with OEM"],
  ["extended",  "Extended",        "Extended-warranty purchased"],
  ["replaced",  "Replaced",        "Device replaced under refresh"],
  ["let_lapse", "Let lapse",       "Coverage will be allowed to lapse"],
  ["dismissed", "Dismissed",       "Not relevant · no action needed"],
];

export default function WarrantyNudges() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("open");
  const [busy, setBusy] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/warehouse/warranty/nudges",
        { params: { status: statusFilter, limit: 200 } });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (outcome) => {
    if (!selected) return;
    try {
      await api.post(`/warehouse/warranty/nudges/${selected.id}/resolve`,
        { outcome, notes: notes.trim() || null });
      toast.success(`Marked ${outcome.replace("_", " ")}`);
      setSelected(null); setNotes("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolve failed");
    }
  };

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="warranty-nudges-page">
      <PageHeader
        eyebrow="Devices · Concierge"
        title="Warranty Renewal Nudges"
        context="Critical devices with warranty ending in the next 25-35 days. One decision per nudge closes the loop."
        actions={
          <div className="flex items-center gap-2">
            <select className="border rounded h-9 px-2 text-[12px]"
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="nudges-status">
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="">All</option>
            </select>
            <Button variant="outline" onClick={load} disabled={busy}
              data-testid="nudges-refresh">
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      <div className="st-panel p-3 flex flex-wrap items-baseline gap-x-4 gap-y-1"
             data-testid="nudges-summary">
        <span className="text-2xl font-heading font-semibold tabular-nums">{total}</span>
        <span className="text-[12px] text-slate-500">
          {statusFilter || "all"} nudges
        </span>
        <span className="ml-auto text-[11px] text-slate-500">
          Refreshed nightly by <code className="text-[10px] px-1 py-0.5 rounded bg-slate-100">warranty-renewal-nudge</code>
        </span>
      </div>

      <Section title="Devices needing a warranty decision">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Device</th>
                <th className="text-left px-3 py-2">Serial</th>
                <th className="text-left px-3 py-2">Warranty ends</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Outcome</th>
                <th className="text-right px-3 py-2 w-40"></th>
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-400">Loading…</td></tr>
              )}
              {!busy && rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-400">
                  Nothing to act on — all clear.
                </td></tr>
              )}
              {!busy && rows.map((n) => {
                const end = n.warranty_end;
                const days = end ? Math.floor((new Date(end) - new Date()) / 86400000) : null;
                return (
                  <tr key={n.id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`nudge-row-${n.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{n.title || n.asset_tag}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{n.serial || "—"}</td>
                    <td className="px-3 py-2 text-[12px]">
                      <span className="inline-flex items-center gap-1 text-amber-800 font-semibold">
                        <ShieldAlert className="h-3 w-3" strokeWidth={1.8} />
                        {end}
                      </span>
                      {days != null && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d away`}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[12px] capitalize">{n.status}</td>
                    <td className="px-3 py-2 text-[12px] capitalize">
                      {(n.outcome || "").replace("_", " ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline"
                          onClick={() => n.asset_id && navigate(`/assets?q=${encodeURIComponent(n.serial || n.asset_tag || "")}`)}
                          data-testid={`nudge-open-${n.id}`}>
                          <ExternalLink className="h-3 w-3 mr-1" /> Open
                        </Button>
                        {n.status === "open" && (
                          <Button size="sm"
                            onClick={() => { setSelected(n); setNotes(""); }}
                            data-testid={`nudge-resolve-${n.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Decide
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setNotes(""); } }}>
        <DialogContent className="max-w-md" data-testid="nudge-decision-dialog">
          <DialogHeader>
            <DialogTitle>Decide on this warranty</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-[13px]">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="font-medium text-slate-800">{selected.title}</div>
                <div className="text-[11px] text-slate-600 mt-1">
                  Serial <span className="font-mono">{selected.serial}</span>
                  {" · "}Ends <b>{selected.warranty_end}</b>
                </div>
              </div>
              <Textarea rows={2} value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional decision note for the audit trail…"
                data-testid="nudge-notes" />
            </div>
          )}
          <DialogFooter className="flex-wrap gap-1">
            {OUTCOMES.map(([v, label]) => (
              <Button key={v} size="sm"
                variant={v === "dismissed" || v === "let_lapse" ? "outline" : "default"}
                onClick={() => resolve(v)}
                data-testid={`nudge-outcome-${v}`}>
                {label}
              </Button>
            ))}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
